import type { FinancialSnapshot, Movement, PendingFundingSourceKind, TemporaryContribution, WeekId } from "./types";

export interface PendingBreakdown {
  billedCents: number;
  coveredCents: number;
  movementPendingCents: number;
  ownPendingCents: number;
  reimbursementPendingCents: number;
  totalPendingCents: number;
}

export interface PendingFundingSource {
  kind: PendingFundingSourceKind;
  sourceId?: string;
  periodId?: string;
}

export type ReimbursementFundingSource = PendingFundingSource;

export interface PendingFundingChange {
  snapshot: FinancialSnapshot;
  contribution?: TemporaryContribution;
  accountDeltaCents: number;
  restoredToUnclassifiedNu: boolean;
}

export type ReimbursementFundingChange = PendingFundingChange;

function availableSavingsCents(snapshot: FinancialSnapshot): number {
  const assigned = Math.max(
    snapshot.protectedSavingsCents,
    snapshot.allocatedSavingsCents ?? snapshot.protectedSavingsCents,
  );
  return Math.max(0, snapshot.savingsCents - assigned);
}

/**
 * Aplica el origen contable de un pendiente de tarjeta.
 * Un delta positivo aparta dinero; uno negativo revierte una confirmación.
 * La reserva de tarjeta se actualiza por separado, dentro de la misma transacción.
 */
export function applyPendingFundingChange(
  snapshot: FinancialSnapshot,
  deltaCents: number,
  source: PendingFundingSource,
  contribution?: TemporaryContribution,
): PendingFundingChange {
  const delta = Math.round(deltaCents);
  if (!Number.isSafeInteger(delta)) throw new Error("El abono debe expresarse en centavos enteros.");
  if (!delta) return { snapshot, contribution, accountDeltaCents: 0, restoredToUnclassifiedNu: false };

  if (source.kind === "new") {
    const balance = snapshot.accounts[0]?.balanceCents ?? 0;
    if (balance + delta < 0) throw new Error("La corrección dejaría el saldo de Nu por debajo de cero.");
    return {
      snapshot: {
        ...snapshot,
        accounts: snapshot.accounts.map((account, index) => index === 0
          ? { ...account, balanceCents: account.balanceCents + delta }
          : account),
      },
      accountDeltaCents: delta,
      restoredToUnclassifiedNu: false,
    };
  }

  if (source.kind === "week") {
    const weekId = source.sourceId as WeekId | undefined;
    const periodMatches = !source.periodId || source.periodId === snapshot.periodLabel;
    const week = snapshot.weeks.find((item) => item.id === weekId);
    if (delta < 0 && (!periodMatches || !week || week.status !== "active")) {
      return { snapshot, accountDeltaCents: 0, restoredToUnclassifiedNu: true };
    }
    if (!periodMatches || !week || week.status !== "active") {
      throw new Error("La semana elegida ya no está activa. Actualiza y elige otra fuente.");
    }
    if (delta > 0 && week.availableCents < delta) throw new Error("La semana activa no tiene saldo suficiente para este abono.");
    if (delta < 0 && (week.spentCents < -delta || (week.pendingPaymentsCents ?? 0) < -delta)) {
      throw new Error("La semana ya no conserva el uso que corresponde a este abono.");
    }
    return {
      snapshot: {
        ...snapshot,
        weeks: snapshot.weeks.map((item) => item.id === week.id ? {
          ...item,
          availableCents: item.availableCents - delta,
          spentCents: item.spentCents + delta,
          pendingPaymentsCents: (item.pendingPaymentsCents ?? 0) + delta,
        } : item),
      },
      accountDeltaCents: 0,
      restoredToUnclassifiedNu: false,
    };
  }

  if (source.kind === "savings") {
    if (delta > 0 && availableSavingsCents(snapshot) < delta) {
      throw new Error("El ahorro disponible no alcanza para este abono.");
    }
    if (snapshot.savingsCents - delta < 0) throw new Error("La corrección dejaría el ahorro por debajo de cero.");
    return {
      snapshot: { ...snapshot, savingsCents: snapshot.savingsCents - delta },
      accountDeltaCents: 0,
      restoredToUnclassifiedNu: false,
    };
  }

  if (!contribution || contribution.id !== source.sourceId) throw new Error("El fondo elegido ya no existe.");
  if (delta > 0 && contribution.active === false) throw new Error("El fondo elegido está archivado.");
  if (delta > 0 && contribution.balanceCents < delta) throw new Error("El fondo elegido no tiene saldo suficiente para este abono.");
  if (snapshot.temporaryContributionCents - delta < 0) throw new Error("El total de fondos disponibles quedaría por debajo de cero.");
  return {
    snapshot: { ...snapshot, temporaryContributionCents: snapshot.temporaryContributionCents - delta },
    contribution: {
      ...contribution,
      active: delta < 0 ? true : contribution.active,
      balanceCents: contribution.balanceCents - delta,
    },
    accountDeltaCents: 0,
    restoredToUnclassifiedNu: false,
  };
}

/** Alias explícito para las correcciones históricas de reembolsos. */
export const applyReimbursementFundingChange = applyPendingFundingChange;

/** Separa el pendiente técnico sin descontar dos veces un reembolso recibido. */
export function pendingBreakdown(movement: Movement): PendingBreakdown {
  const billedCents = Math.max(0, movement.billedAmountCents ?? movement.amountCents);
  const coveredCents = Math.min(billedCents, Math.max(0, movement.coveredAmountCents ?? movement.amountCents));
  const movementPendingCents = Math.max(0, movement.pendingAmountCents ?? billedCents - coveredCents);
  const reimbursementPendingCents = movement.reimbursementReceived
    ? 0
    : Math.min(movementPendingCents, Math.max(0, movement.reimbursementCents ?? 0));
  const ownPendingCents = Math.max(0, movementPendingCents - reimbursementPendingCents);
  return {
    billedCents,
    coveredCents,
    movementPendingCents,
    ownPendingCents,
    reimbursementPendingCents,
    totalPendingCents: ownPendingCents + reimbursementPendingCents,
  };
}

/** Registra dinero de reembolsos que efectivamente entró a Nu y quedó reservado. */
export function creditCardReimbursements(
  snapshot: FinancialSnapshot,
  card: string,
  amountCents: number,
): FinancialSnapshot {
  const amount = Math.round(amountCents);
  if (amount < 0) throw new Error("El reembolso no puede ser negativo.");
  if (!amount) return snapshot;
  let found = false;
  const cardPayments = snapshot.cardPayments.map((item) => {
    if (item.name !== card) return item;
    found = true;
    return { ...item, amountCents: item.amountCents + amount };
  });
  if (!found) cardPayments.push({ name: card, amountCents: amount });
  return {
    ...snapshot,
    accounts: snapshot.accounts.map((account, index) => index === 0
      ? { ...account, balanceCents: account.balanceCents + amount }
      : account),
    cardPayments,
  };
}
